import type { ReactNode } from 'react';

import { css, styled } from 'styled-components';

import { ValidationStatus } from './ValidationStatus';

export const FormControlChildrenCSS = css<{
  $colorVariant?: 'light' | 'dark' | 'twilight';
  $disableResize?: boolean;
  $isValid?: boolean;
}>`
  font-family: ${(props) => props.theme.fontFamily.montserrat};
  display: block;
  width: 100%;
  border-radius: 8px;
  outline: none;
  padding: 0.875rem 1rem;

  line-height: 1.25rem;
  font-size: ${(props) => props.theme.fontSizes.s};
  font-weight: 600;

  ${(props) =>
    props.$isValid === false &&
    `border: 2px solid ${props.theme.colors.alertRed[80]};
  `}

  &::placeholder {
    font-size: ${(props) => props.theme.fontSizes.s};
    font-weight: 400;
  }

  &:focus {
    border: 2px solid ${(props) => props.theme.colors.emeraldGreen[100]};

    ${(props) =>
      props.$isValid &&
      `border: 2px solid ${props.theme.colors.emeraldGreen[100]};
  `}

    ${(props) =>
      props.$isValid === false &&
      `border: 2px solid ${props.theme.colors.alertRed[100]};
  `}
  }
`;

const LabelSC = styled.label`
  display: block;
  font-weight: 400;
  font-size: ${(props) => props.theme.fontSizes.s};
  line-height: 1rem;
`;

const HintSC = styled.p`
  font-size: ${(props) => props.theme.fontSizes.xs};
  line-height: 1rem;
  font-weight: 500;
  margin: 0;
  margin-block-start: 0.25rem;
  padding-inline-start: 0.375rem;
  height: 1rem;
`;

const lightColorVariantCSS = css<{ $isDisabled?: boolean; $isValid?: boolean }>`
  ${LabelSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[30]};
      `}
  }

  ${HintSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[80]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[30]};
      `}
  }
`;

const twilightColorVariantCSS = css<{ $isDisabled?: boolean }>`
  ${LabelSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[30]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[30]};
      `}
  }

  ${HintSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[30]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[30]};
      `}
  }
`;

const darkColorVariantCSS = css<{ $isDisabled?: boolean }>`
  ${LabelSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[30]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[80]};
      `}
  }

  ${HintSC} {
    color: ${(props) => props.theme.colors.oxfordBlue[30]};

    ${(props) =>
      props.$isDisabled &&
      `
        color: ${props.theme.colors.oxfordBlue[80]};
      `}
  }
`;

const FormControlSC = styled.div<{
  $colorVariant?: 'light' | 'dark' | 'twilight';
  $isDisabled?: boolean;
  $isValid?: boolean;
}>`
  height: 5.5rem;

  ${(props) => {
    switch (props.$colorVariant) {
      case 'dark':
        return darkColorVariantCSS;
      case 'twilight':
        return twilightColorVariantCSS;
      default:
        return lightColorVariantCSS;
    }
  }}

  ${LabelSC} {
    ${(props) =>
      props.$isValid === false &&
      `
        color: ${props.theme.colors.alertRed[100]};
      `}
  }

  ${HintSC} {
    ${(props) =>
      props.$isValid === false &&
      `
        color: ${props.theme.colors.alertRed[100]};
        font-weight: 600;
      `}
  }
`;

const LabelContentSC = styled.span`
  display: flex;
  margin-block-end: 0.25rem;
`;

type Props = {
  children?: ReactNode;
  className?: string;
  colorVariant?: 'light' | 'dark' | 'twilight';
  hint?: string;
  isDisabled?: boolean;
  isValid?: boolean;
  label?: string;
  validationPassedLabel?: string;
  validationWarningLabel?: string;
};

export const FormControl = ({
  children,
  className,
  colorVariant,
  hint,
  isDisabled,
  isValid,
  label,
  validationPassedLabel,
  validationWarningLabel,
}: Props) => {
  return (
    <FormControlSC $colorVariant={colorVariant} $isDisabled={isDisabled} $isValid={isValid} className={className}>
      <LabelSC>
        <LabelContentSC>
          {label}
          <ValidationStatus
            isValid={isValid}
            validationPassedLabel={validationPassedLabel}
            validationWarningLabel={validationWarningLabel}
          />
        </LabelContentSC>
        {children}
      </LabelSC>
      <HintSC>{hint}</HintSC>
    </FormControlSC>
  );
};
